import type { Brand } from './brand';

/**
 * The authority axis — the ONLY authority value a read-model observation may carry.
 *
 * DERIVED FROM (verbatim, single-constant enum):
 *   backend/contracts/src/main/java/org/rescue/contract/api/readmodel/
 *     CapabilityDefinitionReadResultV1.java
 *       enum Authority { NON_AUTHORITY }
 *   whose accessor is documented: "Fixed invariant; callers cannot supply or
 *   elevate authority."
 *
 * STRUCTURALLY NON-ELEVATABLE — four independent reasons, each checkable:
 *
 *   1. THERE IS NO SECOND MEMBER. `NonAuthority` is the whole vocabulary. A
 *      second value cannot be constructed without editing this file, and
 *      `./invariants.ts` fails the typecheck if the type stops being exactly
 *      `'NON_AUTHORITY'`.
 *
 *   2. THE TYPE IS NOMINAL. A bare string is not assignable to `NonAuthority`,
 *      so no component, domain projection, fixture or adapter can synthesise one
 *      by writing a literal.
 *
 *   3. THERE IS NO ELEVATION FUNCTION. This module deliberately exports no
 *      `authorize`, `elevate`, `canAct`, `isPermitted` or `grant` helper. Adding
 *      one is a defect under FE-1 Stage Work Item §10, not a feature.
 *
 *   4. IT IS NOT A BOOLEAN. `NonAuthority` is not `true`, not `boolean`, and not
 *      truthy-testable into a permission. There is no predicate that reads it.
 *
 * `Read Model != Authority` (FE-1 Stage Work Item §7.2, §10; builder registry
 * AUTHORITY SEMANTIC PRESERVATION). Nothing in FE1-W0 may render a read result
 * as permission to act.
 */
export type NonAuthority = Brand<'NON_AUTHORITY', 'ReadModelAuthority'>;

/**
 * The one and only authority value.
 *
 * The assertion is the single sanctioned construction site for the brand. It
 * widens nothing: `NonAuthority` is assignable to `'NON_AUTHORITY'`, so the
 * assertion narrows rather than forging a new member.
 */
export const NON_AUTHORITY: NonAuthority = 'NON_AUTHORITY' as NonAuthority;
